/**
 * FleetImportActions — Import/Cancel buttons + summary stats.
 *
 * Props:
 *   vehicles: array of extracted vehicles
 *   flagCount: number of vehicles with unresolved flags
 *   onImport: () => void
 *   onCancel: () => void
 *   importing: boolean
 */
import React from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { vortxColors } from '@vortx/shared-ui';

function FleetImportActions({ vehicles = [], flagCount = 0, onImport, onCancel, importing = false }) {
    const totalVehicles = vehicles.length;
    const totalMiles = vehicles.reduce((sum, v) => sum + (v.daily_miles || 0), 0);
    const totalEnergy = vehicles.reduce((sum, v) => sum + (v.energy_kwh || 0), 0);
    const canImport = flagCount === 0 && totalVehicles > 0;

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mt: 2,
                pt: 2,
                borderTop: `1px solid rgba(178,140,80,0.2)`,
            }}
        >
            <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                <strong>{totalVehicles}</strong> vehicles
                {flagCount > 0 && (
                    <Typography component="span" sx={{ color: '#ed6c02', fontWeight: 600 }}>
                        {' '}&bull; {flagCount} flagged
                    </Typography>
                )}
                {' '}&bull; {totalMiles.toLocaleString()} mi &bull; {Math.round(totalEnergy).toLocaleString()} kWh
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onCancel}
                    disabled={importing}
                    sx={{
                        borderColor: vortxColors.stone,
                        color: vortxColors.stone,
                        '&:hover': { borderColor: vortxColors.white, color: vortxColors.white },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={importing ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                    onClick={onImport}
                    disabled={!canImport || importing}
                    sx={{
                        background: canImport
                            ? `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`
                            : undefined,
                        color: canImport ? '#000' : undefined,
                        fontWeight: 600,
                        '&:hover': {
                            background: canImport
                                ? `linear-gradient(135deg, #c9a060 0%, #e0b584 100%)`
                                : undefined,
                        },
                    }}
                >
                    {importing ? 'Importing...' : `Import ${totalVehicles} Vehicles`}
                </Button>
            </Box>
        </Box>
    );
}

export default FleetImportActions;
